import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toast";
import axiosInstance from "../../../api/axiosInstance";
import { FaTimes, FaCodeBranch } from "react-icons/fa";
import { AccessibleSelect } from "../../common";

const LinkRepositoryModal = ({
  isOpen,
  onClose,
  onSuccess,
  projectId,
  isEditMode = false,
  initialData = null,
}) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [repositories, setRepositories] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState("");
  const [selectedRepository, setSelectedRepository] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingWorkspaces, setFetchingWorkspaces] = useState(false);
  const [fetchingRepos, setFetchingRepos] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchWorkspaces();
      if (isEditMode && initialData) {
        setSelectedWorkspace(initialData.workspace_name?.trim() || "");
        setSelectedRepository(initialData.repo_name || "");
      }
    } else {
      resetForm();
    }
  }, [isOpen, isEditMode, initialData]);

  useEffect(() => {
    if (selectedWorkspace) {
      fetchRepositories(selectedWorkspace);
    } else {
      setRepositories([]);
      setSelectedRepository("");
    }
  }, [selectedWorkspace]);

  const fetchWorkspaces = async () => {
    setFetchingWorkspaces(true);
    try {
      const res = await axiosInstance.get("/git_workspaces");
      if (Array.isArray(res.data)) setWorkspaces(res.data);
    } catch {
      toast.error("Failed to load workspaces");
    } finally {
      setFetchingWorkspaces(false);
    }
  };

  const fetchRepositories = async (workspaceSlug) => {
    setFetchingRepos(true);
    setRepositories([]);
    try {
      const res = await axiosInstance.get(
        `/list_workspace_repos?workspace=${workspaceSlug}`
      );
      if (Array.isArray(res.data)) setRepositories(res.data);
    } catch {
      toast.error("Failed to load repositories");
    } finally {
      setFetchingRepos(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!selectedWorkspace) newErrors.workspace = "Workspace is required";
    if (!selectedRepository) newErrors.repository = "Repository is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const selectedWs = workspaces.find(
        (ws) => ws.slug === selectedWorkspace
      );
      const selectedRepo = repositories.find(
        (repo) => repo.slug === selectedRepository
      );

      const payload = {
        project_id: projectId,
        workspace_name: selectedWs.slug,
        repo_name: selectedRepo.slug,
      };

      if (isEditMode) {
        await axiosInstance.put("/update_project_repo", payload);
      } else {
        await axiosInstance.post("/add_project_repo", payload);
      }

      toast.success(
        isEditMode
          ? "Repository updated successfully"
          : "Repository linked successfully"
      );

      onSuccess();
      onClose();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to save repository link"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setSelectedWorkspace("");
    setSelectedRepository("");
    setWorkspaces([]);
    setRepositories([]);
    setErrors({});
  };

  const workspaceOptions = useMemo(
    () => workspaces.map((ws) => ({ value: ws.slug, label: ws.slug })),
    [workspaces]
  );

  const repositoryOptions = useMemo(
    () => repositories.map((repo) => ({ value: repo.slug, label: repo.name })),
    [repositories]
  );

  if (!isOpen) return null;

  return (
    <div data-rt-modal-overlay className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-md"
      onClick={handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-[fadeIn_.2s_ease]"
      >
        {/* Header */}
        <div className="px-6 py-5 bg-gradient-to-r from-indigo-600 to-blue-500 text-white flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold">
              {isEditMode ? "Update Repository" : "Link Repository"}
            </h2>
            <p className="text-xs opacity-80">
              Connect your project to a repository
            </p>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-white/20 rounded-lg">
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Flow Indicator */}
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span className={selectedWorkspace ? "text-indigo-600 font-medium" : ""}>
              1. Workspace
            </span>
            <span>→</span>
            <span className={selectedRepository ? "text-indigo-600 font-medium" : ""}>
              2. Repository
            </span>
          </div>

          {/* Workspace */}
          <div className="space-y-2">
            <label
              id="link-repo-workspace-label"
              htmlFor="link-repo-workspace"
              className="text-sm text-gray-600 font-medium"
            >
              Select Workspace
            </label>
            <AccessibleSelect
              id="link-repo-workspace"
              value={selectedWorkspace}
              onChange={(value) => {
                setSelectedWorkspace(value);
                setErrors((p) => ({ ...p, workspace: "" }));
              }}
              options={workspaceOptions}
              placeholder={
                fetchingWorkspaces ? "Loading..." : "Choose workspace"
              }
              disabled={loading || fetchingWorkspaces}
              error={Boolean(errors.workspace)}
              aria-labelledby="link-repo-workspace-label"
            />
            {errors.workspace && (
              <p className="text-xs text-red-500">{errors.workspace}</p>
            )}
          </div>

          {/* Repository */}
          <div className="space-y-2">
            <label
              id="link-repo-repository-label"
              htmlFor="link-repo-repository"
              className="text-sm text-gray-600 font-medium"
            >
              Select Repository
            </label>
            <AccessibleSelect
              id="link-repo-repository"
              value={selectedRepository}
              onChange={(value) => {
                setSelectedRepository(value);
                setErrors((p) => ({ ...p, repository: "" }));
              }}
              options={repositoryOptions}
              placeholder={
                !selectedWorkspace
                  ? "Select workspace first"
                  : fetchingRepos
                  ? "Loading..."
                  : "Choose repository"
              }
              disabled={!selectedWorkspace || fetchingRepos || loading}
              error={Boolean(errors.repository)}
              aria-labelledby="link-repo-repository-label"
            />
            {errors.repository && (
              <p className="text-xs text-red-500">{errors.repository}</p>
            )}
          </div>

          {/* Selected Preview */}
          {(selectedWorkspace || selectedRepository) && (
            <div className="bg-gray-50 p-3 rounded-lg text-sm flex items-center gap-2">
              <FaCodeBranch className="text-gray-500" />
              <span>
                {selectedWorkspace || "..."} /{" "}
                {selectedRepository || "..."}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm bg-gray-200 rounded-lg hover:bg-gray-300"
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            disabled={loading || fetchingRepos || fetchingWorkspaces}
            className="px-5 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50"
          >
            {loading && (
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {isEditMode ? "Update" : "Link Repository"}
          </button>
        </div>

        <style>
          {`
            @keyframes fadeIn {
              from { opacity: 0; transform: translateY(10px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default LinkRepositoryModal;